// ===== FILE: src/pages/Listing/ContractorCategoryList.tsx ===== (UPDATED)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Input } from 'reactstrap';
import { Link } from 'react-router-dom';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import SEOTags from './SEOTags';
import { useContractorCategories } from '../../hooks/useContractorCategories';
import { buildCategoryUrl, createSlug } from '../../utils/urlUtils';
import Header from "../Home/Header";
import Footer from "../Home/Footer";

const ContractorCategoryList: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortBy, setSortBy] = useState<'name' | 'count'>('count');
    
    // Get all categories with counts
    const { 
        categories, 
        loading, 
        error, 
        refetch 
    } = useContractorCategories(1000); // Get all categories

    // Filter and sort categories
    const filteredAndSortedCategories = React.useMemo(() => {
        let filtered = categories;

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = categories.filter(category =>
                category.category.toLowerCase().includes(term)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            if (sortBy === 'count') {
                return b.count - a.count; // Descending by count
            } else {
                return a.category.localeCompare(b.category); // Ascending by name
            }
        });

        return filtered;
    }, [categories, searchTerm, sortBy]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSortBy(e.target.value as 'name' | 'count');
    };

    // Group categories for better display
    const groupedCategories = React.useMemo(() => {
        const groups: { [key: string]: typeof filteredAndSortedCategories } = {};
        
        filteredAndSortedCategories.forEach(category => {
            const firstLetter = category.category.charAt(0).toUpperCase();
            if (!groups[firstLetter]) {
                groups[firstLetter] = [];
            }
            groups[firstLetter].push(category);
        });

        return groups;
    }, [filteredAndSortedCategories]);

    const alphabeticalOrder = Object.keys(groupedCategories).sort();

    return (
        <React.Fragment>
            <Header isFullWidth={true} />
            <div className="page-content">
                <SEOTags
                    title="Contractor Categories Directory"
                    description="Browse contractors by category. Find specialists in construction, home improvement, landscaping, and more professional services."
                    schemaType="ItemList"
                />

                <Container fluid>
                    <AutoBreadCrumb />

                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="align-items-center">
                                        <Col md={6}>
                                            <h4 className="card-title mb-0">
                                                Contractor Categories 
                                                {!loading && (
                                                    <span className="text-muted ms-2">
                                                        ({filteredAndSortedCategories.length} categories)
                                                    </span>
                                                )}
                                            </h4>
                                        </Col>
                                        <Col md={6}>
                                            <Row>
                                                <Col md={7}>
                                                    <Input
                                                        type="text"
                                                        placeholder="Search categories..."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                    />
                                                </Col>
                                                <Col md={5}>
                                                    <select
                                                        className="form-select"
                                                        value={sortBy}
                                                        onChange={handleSortChange}
                                                    >
                                                        <option value="count">Sort by Count</option>
                                                        <option value="name">Sort by Name</option>
                                                    </select>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </CardHeader>

                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center p-4">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : error ? (
                                        <div className="alert alert-danger">{error}</div>
                                    ) : filteredAndSortedCategories.length > 0 ? (
                                        <>
                                            {/* Quick stats */}
                                            <div className="row mb-4">
                                                <div className="col-md-12">
                                                    <div className="bg-light p-3 rounded">
                                                        <div className="row text-center">
                                                            <div className="col-md-4">
                                                                <h5 className="mb-1">{categories.length}</h5>
                                                                <p className="text-muted mb-0">Total Categories</p>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <h5 className="mb-1">
                                                                    {categories.reduce((sum, cat) => sum + cat.count, 0)}
                                                                </h5>
                                                                <p className="text-muted mb-0">Total Contractors</p>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <h5 className="mb-1">
                                                                    {Math.round(categories.reduce((sum, cat) => sum + cat.count, 0) / categories.length)}
                                                                </h5>
                                                                <p className="text-muted mb-0">Avg per Category</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Category listing */}
                                            {sortBy === 'name' ? (
                                                // Alphabetical grouping
                                                alphabeticalOrder.map(letter => (
                                                    <div key={letter} className="mb-4">
                                                        <h5 className="text-primary border-bottom pb-2 mb-3">{letter}</h5>
                                                        <Row>
                                                            {groupedCategories[letter].map((category) => (
                                                                <Col md={6} lg={4} xl={3} className="mb-3" key={category.category}>
                                                                    <div className="card border h-100 category-card">
                                                                        <div className="card-body">
                                                                            <Link
                                                                                to={buildCategoryUrl(category.category)}
                                                                                className="text-decoration-none"
                                                                            >
                                                                                <h6 className="card-title text-primary mb-1">
                                                                                    {category.category}
                                                                                </h6>
                                                                            </Link>
                                                                            <p className="card-text text-muted mb-0">
                                                                                {category.count} contractor{category.count !== 1 ? 's' : ''}
                                                                            </p>
                                                                            <div className="mt-2">
                                                                                <div className="progress" style={{ height: '3px' }}>
                                                                                    <div 
                                                                                        className="progress-bar bg-primary" 
                                                                                        style={{ 
                                                                                            width: `${(category.count / Math.max(...categories.map(c => c.count))) * 100}%` 
                                                                                        }}
                                                                                    ></div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </div>
                                                ))
                                            ) : (
                                                // Count-based listing
                                                <Row>
                                                    {filteredAndSortedCategories.map((category, index) => (
                                                        <Col md={6} lg={4} xl={3} className="mb-3" key={category.category}>
                                                            <div className={`card border h-100 category-card ${index < 3 ? 'border-primary' : ''}`}>
                                                                <div className="card-body">
                                                                    {index < 3 && (
                                                                        <div className="badge bg-primary position-absolute top-0 end-0 m-2">
                                                                            #{index + 1}
                                                                        </div>
                                                                    )}
                                                                    <Link
                                                                        to={buildCategoryUrl(category.category)}
                                                                        className="text-decoration-none"
                                                                    >
                                                                        <h6 className="card-title text-primary mb-1">
                                                                            {category.category}
                                                                        </h6>
                                                                    </Link>
                                                                    <p className="card-text text-muted mb-2">
                                                                        {category.count} contractor{category.count !== 1 ? 's' : ''}
                                                                    </p>
                                                                    <div className="progress" style={{ height: '4px' }}>
                                                                        <div 
                                                                            className="progress-bar bg-primary" 
                                                                            style={{ 
                                                                                width: `${(category.count / Math.max(...categories.map(c => c.count))) * 100}%` 
                                                                            }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-muted">No categories found matching your search.</p>
                                            {searchTerm && (
                                                <button 
                                                    className="btn btn-outline-primary"
                                                    onClick={() => setSearchTerm('')}
                                                >
                                                    Clear Search
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Footer />

            <style>{`
                .category-card {
                    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                    position: relative;
                }
                
                .category-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                
                .progress {
                    border-radius: 2px;
                }
            `}</style>
        </React.Fragment>
    );
};

export default ContractorCategoryList;